import { useAppStore } from "@/store";
import { useTabPageStore } from "@/components/layout/components/tab-bar/use-tab-page-store";
import { observer } from "mobx-react-lite";
import {
  MDSSelect,
  MDSIcon,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSBadge,
  MDSBadgeVariant,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  MDSTextColor,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSIconButtonVariant,
  MDSIconButtonSize,
  MDSIconKind,
  MDSMenuContentItem,
  MDSMenuItemVariant,
  MDSMenuContentItemKind,
} from "@/mds";
import { DateTime } from "luxon";
import { Key, useCallback } from "react";
import { MDSBanner } from "@/mds/components/banner/banner";
import { MDSBannerKind, MDSBannerSlotName } from "@/mds/components/banner/banner.types";
import { generateVersionDate } from "@/domains/date/date";
import { Uuid } from "@/domains/global/identifiers";
import { pluralize } from "@/modules/pluralize/pluralize";
import useMeasure from "react-use/lib/useMeasure";
import {
  LeftSlotContainer,
  RightSlotContainer,
  VersionSelectWrapper,
  StyledArrowButton,
  StyledRightButton,
  VersionHistoryBannerWrapper,
} from "./version-history.styles";
import { NoteVersionSnapshotSummary } from "@/store/note-version-history/types";
import { formatSnapshotAttribution } from "./format-snapshot-attribution";
import { MEM_SPACE_ACCOUNT_ID } from "@/store/note-version-history/constants";

const RESTORE_VERSION_BUTTON_LABEL = "Restore version";
const RETURN_TO_EDITING_BUTTON_LABEL = "Return to editing";

export const NoteVersionHistoryMenuBar = observer(function NoteVersionHistoryMenuBar() {
  const { store } = useAppStore();
  const page = useTabPageStore("notesViewPage");
  const { hasIconsOnly, hasCompactText, hasNoNavButton, containerRef } = useContainerBreakpoints();

  const isActive = typeof page.noteVersionHistory.currentNoteId === "string";
  if (!isActive) return null;

  const noteVersionHistory = page.noteVersionHistory;

  const selectedKey = noteVersionHistory.selectedSnapshot?.id || "";

  const buildAttributionLabel = (s: NoteVersionSnapshotSummary): string => {
    const editCount = s.edit_count ?? 1;
    const editorId = s.editor_space_account_id;
    const isMe = editorId === store.spaceAccounts.myPersonalSpaceAccountId;
    const isMem = editorId?.toLowerCase() === MEM_SPACE_ACCOUNT_ID.toLowerCase();

    const spaceAccount = editorId
      ? store.spaceAccounts.getSpaceAccount(editorId as unknown as Uuid)
      : undefined;
    const contact = editorId
      ? store.contacts.getBySpaceAccountId(editorId as unknown as Uuid)
      : undefined;
    const authorName = isMe
      ? store.account.myAccount?.displayName || "You"
      : isMem
        ? "Mem"
        : spaceAccount?.profileDisplayName || contact?.profileDisplayName || "";

    return formatSnapshotAttribution({
      editCount,
      authorName,
      isFirstVersion: s.note_content_document_version === 1,
      contextKind: s.editor_context_kind,
      isAttributionEnabled: true,
    });
  };

  const versionItems: MDSMenuContentItem[] = noteVersionHistory.snapshots.map(
    (s: NoteVersionSnapshotSummary) => {
      const editCount = s.edit_count ?? 1;
      const editBy = buildAttributionLabel(s);

      const isViewing = s.id === noteVersionHistory.selectedSnapshot?.id;

      const baseDateText = generateVersionDate(DateTime.fromISO(s.created_at), {
        separator: "comma",
        abbreviateMonthWithYear: true,
        compact: hasCompactText,
      });
      const editsOnlyText = `${editCount} ${pluralize(editCount, "edit", "edits")}`;
      const textValue = hasCompactText
        ? `${baseDateText} · ${editsOnlyText}`
        : `${baseDateText} · ${editBy}`;

      const item: MDSMenuContentItem = {
        id: s.id,
        kind: MDSMenuContentItemKind.Item,
        variant: MDSMenuItemVariant.TwoLine,
        label: generateVersionDate(DateTime.fromISO(s.created_at), { separator: "dot" }),
        subtitle: editBy,
        textValue,
        rightAddon: isViewing
          ? () => (
              <MDSBadge label="Viewing" variant={MDSBadgeVariant.Emphasis} icon={MDSIconKind.Eye} />
            )
          : undefined,
      };

      return item;
    }
  );
  const versionItemsWithHeader: MDSMenuContentItem[] = [
    {
      id: "version-history-header",
      kind: MDSMenuContentItemKind.Header,
      label: "Version history",
    },
    ...versionItems,
  ];

  const selectedSnapshot = noteVersionHistory.selectedSnapshot;
  const selectedTriggerText = selectedSnapshot
    ? (() => {
        const dateText = generateVersionDate(DateTime.fromISO(selectedSnapshot.created_at), {
          separator: "comma",
          abbreviateMonthWithYear: true,
          compact: hasCompactText,
        });
        const attribution = buildAttributionLabel(selectedSnapshot);
        return hasCompactText ? dateText : `${dateText} · ${attribution}`;
      })()
    : "";

  const handleVersionSelect = (key: Key | null) => {
    const idx = noteVersionHistory.snapshots.findIndex(
      (s: NoteVersionSnapshotSummary) => s.id === key
    );
    if (idx !== -1) noteVersionHistory.selectNoteSnapshot(idx);
  };

  const handleRestore = async () => {
    await noteVersionHistory.restoreSelectedNoteSnapshot();
  };

  // Selection helpers for up/down version navigation
  const selectedIndex = noteVersionHistory.selectedSnapshot
    ? noteVersionHistory.snapshots.findIndex(
        (s: NoteVersionSnapshotSummary) => s.id === noteVersionHistory.selectedSnapshot?.id
      )
    : -1;
  const canGoNewer = selectedIndex > 0; // index 0 is newest
  const canGoOlder = selectedIndex >= 0 && selectedIndex < noteVersionHistory.snapshots.length - 1;

  const goNewer = () => {
    if (!canGoNewer) return;
    noteVersionHistory.selectNoteSnapshot(selectedIndex - 1);
  };

  const goOlder = () => {
    if (!canGoOlder) return;
    noteVersionHistory.selectNoteSnapshot(selectedIndex + 1);
  };

  const renderRestoreButton = () => {
    if (hasIconsOnly) {
      return (
        <StyledArrowButton
          aria-label={RESTORE_VERSION_BUTTON_LABEL}
          iconKind={MDSIconKind.History}
          isDisabled={!noteVersionHistory.canRestore}
          isLoading={noteVersionHistory.isLoadingContent}
          onClick={handleRestore}
          size={MDSIconButtonSize.Medium}
          variant={MDSIconButtonVariant.Default}
        />
      );
    }

    return (
      <StyledRightButton
        onClick={handleRestore}
        isDisabled={!noteVersionHistory.canRestore}
        isLoading={noteVersionHistory.isLoadingContent}
        variant={MDSButtonVariant.EmphasisLight}
        aria-label={RESTORE_VERSION_BUTTON_LABEL}
      >
        <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.History} />
        {RESTORE_VERSION_BUTTON_LABEL}
      </StyledRightButton>
    );
  };

  const renderReturnToEditingButton = () => {
    if (hasIconsOnly) {
      return (
        <StyledArrowButton
          aria-label={RETURN_TO_EDITING_BUTTON_LABEL}
          iconKind={MDSIconKind.Exit}
          onClick={() => noteVersionHistory.closeNoteVersionHistory()}
          size={MDSIconButtonSize.Medium}
          variant={MDSIconButtonVariant.Default}
        />
      );
    }

    return (
      <StyledRightButton
        onClick={() => noteVersionHistory.closeNoteVersionHistory()}
        variant={MDSButtonVariant.EmphasisLight}
        aria-label={RETURN_TO_EDITING_BUTTON_LABEL}
      >
        <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Exit} />
        {RETURN_TO_EDITING_BUTTON_LABEL}
      </StyledRightButton>
    );
  };

  return (
    <VersionHistoryBannerWrapper>
      <MDSBanner kind={MDSBannerKind.Info} ref={containerRef}>
        <LeftSlotContainer slot={MDSBannerSlotName.Left}>
          {!hasNoNavButton && (
            <StyledArrowButton
              iconKind={MDSIconKind.AngleUpSolid}
              isDisabled={!canGoNewer}
              onClick={goNewer}
              data-test-id="version-history-up-button"
              size={MDSIconButtonSize.Medium}
              variant={MDSIconButtonVariant.Default}
            />
          )}
          {!hasNoNavButton && (
            <StyledArrowButton
              iconKind={MDSIconKind.AngleDownSolid}
              isDisabled={!canGoOlder}
              onClick={goOlder}
              data-test-id="version-history-down-button"
              size={MDSIconButtonSize.Medium}
              variant={MDSIconButtonVariant.Default}
            />
          )}
          <VersionSelectWrapper>
            <MDSSelect
              label="Version"
              selectedKey={selectedKey}
              onSelectionChange={handleVersionSelect}
              isDisabled={noteVersionHistory.isLoadingContent}
              items={versionItemsWithHeader}
              popoverProps={{ offset: 8 }}
              buttonVariant={MDSButtonVariant.EmphasisLight}
              renderSelectValue={props => {
                return (
                  <MDSText
                    variant={MDSTextVariant.TextSm}
                    color={MDSTextColor.Emphasis}
                    weight={MDSTextWeight.Smedium}
                  >
                    {selectedTriggerText || props.selectedText}
                  </MDSText>
                );
              }}
            />
          </VersionSelectWrapper>
        </LeftSlotContainer>
        <RightSlotContainer slot={MDSBannerSlotName.Right}>
          <MDSTooltip
            config={{
              label: RESTORE_VERSION_BUTTON_LABEL,
              placement: MDSTooltipPlacement.Top,
            }}
          >
            {renderRestoreButton()}
          </MDSTooltip>
          <MDSTooltip
            config={{
              label: RETURN_TO_EDITING_BUTTON_LABEL,
              placement: MDSTooltipPlacement.Top,
            }}
          >
            {renderReturnToEditingButton()}
          </MDSTooltip>
        </RightSlotContainer>
      </MDSBanner>
    </VersionHistoryBannerWrapper>
  );
});

const useContainerBreakpoints = () => {
  const MEDIUM_BP = 785; // right-side buttons become icon-only below this
  const SMALL_BP = 600; // select label becomes compact below this
  const MOBILE_BP = 420; // hide up/down history nav below this

  const [measureRef, { width: measuredWidth }] = useMeasure<HTMLDivElement>();
  const containerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) measureRef(node);
    },
    [measureRef]
  );

  const hasIconsOnly = measuredWidth > 0 && measuredWidth < MEDIUM_BP;
  const hasCompactText = measuredWidth > 0 && measuredWidth < SMALL_BP;
  const hasNoNavButton = measuredWidth > 0 && measuredWidth < MOBILE_BP;

  return {
    hasIconsOnly,
    hasCompactText,
    hasNoNavButton,
    containerRef,
  };
};
